import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../Shared/Services/auth.service';
import { AccessDecisionRouterService } from '../../../Shared/Services/access-decision-router.service';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _auth = inject(AuthService);
  private readonly _accessDecisionRouter = inject(AccessDecisionRouterService);
  private readonly _translate = inject(TranslateService);

  readonly steps = signal<AuthStep[]>([
    { id: 'params', label: 'app.auth.callback.steps.params', state: 'pending' },
    { id: 'exchange', label: 'app.auth.callback.steps.exchange', state: 'pending' },
    { id: 'profile', label: 'app.auth.callback.steps.profile', state: 'pending' },
    { id: 'redirect', label: 'app.auth.callback.steps.redirect', state: 'pending' }
  ]);

  readonly errorDetail = signal<string | null>(null);

  readonly progress = computed(() => {
    const allSteps = this.steps();
    const total = allSteps.length;

    const completed = allSteps.filter(s => s.state === 'completed').length;

    const isLoading = allSteps.some(s => s.state === 'loading');

    const score = completed + (isLoading ? 0.5 : 0);

    return (score / total) * 100;
  });

  readonly activeMessage = computed(() => {
    const allSteps = this.steps();

    if (this.errorDetail()) return this._translate.instant('app.auth.callback.status.auth_failed');

    const activeStep = allSteps.find(s => s.state === 'loading')
      || allSteps.find(s => s.state === 'pending');

    return activeStep ? activeStep.label : this._translate.instant('app.auth.callback.status.redirecting');
  });

  readonly hasError = computed(() =>
    this.steps().some(step => step.state === 'error')
  );

  ngOnInit(): void {
    this.executeAuthSequence();
  }

  private async executeAuthSequence(): Promise<void> {

    this.updateStep('params', 'loading');

    await this.delay(600);

    const code = this._route.snapshot.queryParamMap.get('code');
    const errorMsg = this._route.snapshot.queryParamMap.get('error');

    if (errorMsg) {
      const translationKey = 'shared.api.errors.' + decodeURIComponent(errorMsg);
      this.failStep('params', translationKey);
      return;
    }

    if (!code) {
      this.failStep('params', 'shared.api.errors.invalid_auth_code');
      return;
    }

    this.updateStep('params', 'completed');


    this.updateStep('exchange', 'loading');

    this._auth.exchangeCode(code).subscribe({
      next: () => {
        this.updateStep('exchange', 'completed');
        this.handleSuccessSequence();
      },
      error: (err: HttpErrorResponse) => {
        const apiMessage = err?.error?.message;
        const translationKey = apiMessage || 'app.auth.callback.errors.token_exchange_failed';
        this.failStep('exchange', translationKey);
      }
    });
  }

  private async handleSuccessSequence(): Promise<void> {

    this.updateStep('profile', 'loading');
    await this.delay(800);
    this.updateStep('profile', 'completed');

    this.updateStep('redirect', 'loading');
    await this.delay(600);

    const target = this.resolvePostLoginTarget();
    this.updateStep('redirect', 'completed');

    this._router.navigateByUrl(target);
  }

  private resolvePostLoginTarget(): string {
    return this._accessDecisionRouter.resolveTokenRoute() ?? this._auth.getAndClearReturnUrl();
  }

  private updateStep(id: string, state: 'pending' | 'loading' | 'completed' | 'error'): void {
    this.steps.update(current =>
      current.map(step => step.id === id ? { ...step, state } : step)
    );
  }

  private failStep(id: string, message: string): void {
    this.updateStep(id, 'error');
    this.errorDetail.set(message);
  }

  returnToLogin(): void {
    this._router.navigate(['/auth/login']);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}